import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { Loader2 } from "lucide-react";
import { useCompanyAutocomplete } from "@/hooks/useCompanyAutocomplete";

type Props = {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
};

export function CompanyAutocompleteInput({
  value,
  onValueChange,
  placeholder,
  className,
  disabled,
}: Props) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [anchorWidth, setAnchorWidth] = useState<number | null>(null);

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const blurCloseTimeoutRef = useRef<number | null>(null);

  const enabled = open && value.trim().length >= 3 && !disabled;
  const { suggestions, isLoading } = useCompanyAutocomplete({
    query: value,
    enabled,
    minChars: 3,
    debounceMs: 350,
    maxSuggestions: 8,
  });

  const visibleSuggestions = useMemo(() => {
    const v = value.trim().toLowerCase();
    // light client-side filter to keep list tight and reduce visual noise
    return suggestions.filter((s) => s.toLowerCase().includes(v));
  }, [suggestions, value]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [value]);

  useEffect(() => {
    const el = anchorRef.current;
    if (!el) return;

    const update = () => setAnchorWidth(el.getBoundingClientRect().width);
    update();

    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      if (blurCloseTimeoutRef.current !== null) {
        window.clearTimeout(blurCloseTimeoutRef.current);
      }
    };
  }, []);

  function close() {
    setOpen(false);
    setActiveIndex(-1);
  }

  function choose(s: string) {
    onValueChange(s);
    close();
    // keep UX tight: keep focus in input after picking
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }

  const showDropdown =
    open &&
    value.trim().length >= 3 &&
    (isLoading || visibleSuggestions.length > 0);

  return (
    <Popover open={showDropdown} onOpenChange={(next) => (next ? null : close())}>
      <PopoverAnchor asChild>
        <div ref={anchorRef} className="relative">
          <Input
            ref={inputRef}
            disabled={disabled}
            placeholder={placeholder}
            value={value}
            onChange={(e) => {
              const next = e.target.value;
              onValueChange(next);
              if (disabled) return;
              if (next.trim().length >= 3) setOpen(true);
              else close();
            }}
            onKeyDown={(e) => {
              if (!open && e.key === "ArrowDown" && value.trim().length >= 3) {
                setOpen(true);
                e.preventDefault();
                return;
              }

              if (!showDropdown) return;

              if (e.key === "Escape") {
                e.preventDefault();
                close();
                return;
              }

              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveIndex((i) =>
                  Math.min(i + 1, Math.max(visibleSuggestions.length - 1, 0))
                );
                return;
              }

              if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveIndex((i) => Math.max(i - 1, 0));
                return;
              }

              if (e.key === "Enter" && activeIndex >= 0) {
                e.preventDefault();
                const picked = visibleSuggestions[activeIndex];
                if (picked) choose(picked);
              }
            }}
            onBlur={() => {
              // allow click selection before closing
              if (blurCloseTimeoutRef.current !== null) {
                window.clearTimeout(blurCloseTimeoutRef.current);
              }
              blurCloseTimeoutRef.current = window.setTimeout(() => close(), 120);
            }}
            className={cn("bg-background/50 pr-10", className)}
          />

          {isLoading && value.trim().length >= 3 ? (
            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : null}
        </div>
      </PopoverAnchor>

      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        className="p-0"
        style={anchorWidth ? { width: `${anchorWidth}px` } : undefined}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <CommandList className="max-h-[240px]">
            <CommandEmpty className="py-3 text-sm text-muted-foreground">
              {isLoading ? "Searching…" : "No matches"}
            </CommandEmpty>
            <CommandGroup>
              {visibleSuggestions.map((s, idx) => (
                <CommandItem
                  key={`${s}-${idx}`}
                  value={s}
                  onMouseDown={(e) => e.preventDefault()}
                  onSelect={() => choose(s)}
                  className={cn(
                    "cursor-pointer",
                    idx === activeIndex && "bg-accent text-accent-foreground"
                  )}
                >
                  {s}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}


